import { Link } from "react-router-dom";
import {
  ShieldAlert,
  RefreshCw,
  Sparkles,
  Landmark,
  PieChart,
  ArrowUpRight,
  TrendingUp,
  Gauge,
} from "lucide-react";
import "./PortfolioAnalysis.css";

const REC_ICONS = {
  risk: ShieldAlert,
  rebalance: RefreshCw,
  funds: Sparkles,
  tax: Landmark,
  alloc: PieChart,
};

function formatINR(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

function ScoreRing({ score, label, sub }) {
  const r = 54;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="pa-ring">
      <svg viewBox="0 0 140 140" className="pa-ring__svg" aria-hidden>
        <circle cx="70" cy="70" r={r} className="pa-ring__track" />
        <circle
          cx="70"
          cy="70"
          r={r}
          className="pa-ring__progress"
          style={{
            strokeDasharray: c,
            strokeDashoffset: offset,
          }}
        />
      </svg>
      <div className="pa-ring__value">
        <strong>{score}</strong>
        <span>{label}</span>
        {sub && <em>{sub}</em>}
      </div>
    </div>
  );
}

function AllocBars({ title, items }) {
  return (
    <div className="pa-alloc pm-card">
      <h3>{title}</h3>
      <div className="pa-alloc__stack" aria-hidden>
        {items.map((item) => (
          <div
            key={item.label}
            className="pa-alloc__seg"
            style={{ width: `${item.pct}%`, background: item.color }}
            title={`${item.label}: ${item.pct}%`}
          />
        ))}
      </div>
      <ul className="pa-alloc__legend">
        {items.map((item) => (
          <li key={item.label}>
            <span style={{ background: item.color }} />
            <strong>{item.label}</strong>
            <em>{item.pct}%</em>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PortfolioAnalysis({ data, meta }) {
  const {
    healthScore,
    healthLabel,
    riskScore,
    riskBlurb,
    diversification,
    diversificationBlurb,
    xirr,
    cagr,
    expectedReturn,
    returnNote,
    assets,
    sectors,
    benchmarks,
    recommendations,
    holdings,
    totalValue,
    totalInvested,
    absoluteReturn,
  } = data;

  return (
    <section className="pa-section" id="analysis">
      <div className="pm-container">
        <div className="pa-header">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            Analysis Ready
          </span>
          <h2 className="pm-section-title">Your portfolio snapshot</h2>
          <p className="pm-section-sub">
            {meta?.fileName
              ? `Computed from “${meta.fileName}”`
              : `Computed from ${meta?.holdingsCount || holdings?.length || 0} holding${
                  (meta?.holdingsCount || holdings?.length || 0) > 1 ? "s" : ""
                }`}
            {totalValue ? ` · Total value ${formatINR(totalValue)}` : ""}.
            Scores use your holdings, category detection, and concentration —
            not random demo numbers.
          </p>
          {meta?.warnings?.length > 0 && (
            <ul className="pa-warnings">
              {meta.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="pa-scores">
          <div className="pa-score-card pm-card pa-score-card--hero">
            <ScoreRing
              score={healthScore}
              label="Health Score"
              sub={healthLabel}
            />
            <p>
              Overall quality from diversification, concentration, and asset mix
              balance.
            </p>
          </div>
          <div className="pa-score-card pm-card">
            <div className="pa-metric">
              <Gauge size={20} />
              <span>Risk Score</span>
            </div>
            <strong className="pa-metric__val">
              {riskScore}
              <small>/100</small>
            </strong>
            <div className="pa-bar">
              <div style={{ width: `${riskScore}%` }} />
            </div>
            <p>{riskBlurb}</p>
          </div>
          <div className="pa-score-card pm-card">
            <div className="pa-metric">
              <PieChart size={20} />
              <span>Diversification</span>
            </div>
            <strong className="pa-metric__val">
              {diversification}
              <small>/100</small>
            </strong>
            <div className="pa-bar pa-bar--alt">
              <div style={{ width: `${diversification}%` }} />
            </div>
            <p>{diversificationBlurb}</p>
          </div>
        </div>

        <div className="pa-returns">
          {[
            {
              label: absoluteReturn != null ? "Est. XIRR proxy" : "Est. Return",
              value: `${xirr}%`,
              icon: TrendingUp,
            },
            { label: "Expected Return", value: `${expectedReturn}%`, icon: Sparkles },
            { label: "Est. CAGR", value: `${cagr}%`, icon: TrendingUp },
            ...(absoluteReturn != null
              ? [
                  {
                    label: "Absolute Return",
                    value: `${absoluteReturn > 0 ? "+" : ""}${absoluteReturn}%`,
                    icon: TrendingUp,
                  },
                ]
              : []),
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="pa-return-card pm-card">
              <Icon size={18} />
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        <p className="pa-return-note">{returnNote}</p>
        {totalInvested != null && (
          <p className="pa-return-note">
            Invested {formatINR(totalInvested)} → Current {formatINR(totalValue)}
          </p>
        )}

        <div className="pa-grid">
          <AllocBars title="Asset Allocation" items={assets} />
          <AllocBars title="Style / Category Mix" items={sectors} />
        </div>

        {holdings?.length > 0 && (
          <div className="pa-holdings pm-card">
            <h3>Holdings breakdown</h3>
            <div className="pa-holdings__wrap">
              <table>
                <thead>
                  <tr>
                    <th>Fund</th>
                    <th>Asset</th>
                    <th>Style</th>
                    <th>Value</th>
                    <th>Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {holdings.map((h) => (
                    <tr key={`${h.fundName}-${h.weight}`}>
                      <td>{h.fundName}</td>
                      <td>{h.asset}</td>
                      <td>{h.style}</td>
                      <td>{formatINR(h.amount)}</td>
                      <td>{h.weight}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="pa-bench pm-card">
          <h3>Benchmark Comparison</h3>
          <div className="pa-bench__rows">
            {benchmarks.map((b) => (
              <div key={b.name} className="pa-bench__row">
                <div className="pa-bench__labels">
                  <strong>{b.name}</strong>
                  <span>
                    You {b.you}% · Bench {b.bench}%
                  </span>
                </div>
                <div className="pa-bench__bars">
                  <div
                    className="pa-bench__you"
                    style={{ width: `${Math.min(b.you * 5, 100)}%` }}
                  />
                  <div
                    className="pa-bench__ref"
                    style={{ width: `${Math.min(b.bench * 5, 100)}%` }}
                  />
                </div>
                <em className={b.you >= b.bench ? "up" : "down"}>
                  {b.you >= b.bench ? "+" : ""}
                  {(b.you - b.bench).toFixed(1)}%
                </em>
              </div>
            ))}
          </div>
        </div>

        <div className="pa-recs">
          <h3 className="pa-recs__title">Recommendations</h3>
          <div className="pa-recs__grid">
            {(recommendations || []).map((rec) => {
              const Icon = REC_ICONS[rec.key] || Sparkles;
              return (
                <article key={rec.key || rec.title} className="pa-rec pm-card">
                  <div className="pa-rec__icon">
                    <Icon size={20} />
                  </div>
                  <h4>{rec.title}</h4>
                  <p>{rec.desc}</p>
                </article>
              );
            })}
          </div>
        </div>

        <div className="pa-cta">
          <Link to="/contact" className="pm-btn pm-btn-primary">
            Book Free Portfolio Review
            <ArrowUpRight size={16} />
          </Link>
          <p>
            Talk to a Poonji Mitra advisor for exact XIRR from CAS transactions —
            confidential and no obligation.
          </p>
        </div>
      </div>
    </section>
  );
}
